/**
 * VAT Returns Page
 * 
 * Main page for managing VAT (ALV) returns.
 * Features:
 * - VAT period calendar
 * - List of VAT returns
 * - Create new VAT return wizard
 * - Preview and file returns
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../hooks/useOrganization';
import {
  listVatReturns,
  createVatReturn,
  previewVatCalculation,
  getVatPeriods,
  type VatReturn,
  type VatPeriod,
  type VatReturnCalculation,
} from '../api';
import VatBreakdownDetail from '../components/VatBreakdownDetail';

export default function VatReturnsPage() {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();
  const [activeTab, setActiveTab] = useState<'calendar' | 'list' | 'wizard' | 'detail'>('calendar');
  const [vatReturns, setVatReturns] = useState<VatReturn[]>([]);
  const [periods, setPeriods] = useState<VatPeriod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<VatReturnCalculation | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<VatPeriod | null>(null);
  const [selectedVatReturnId, setSelectedVatReturnId] = useState<string | null>(null);

  // Form state for wizard
  const [wizardStep, setWizardStep] = useState(1);
  const [reportingBasis, setReportingBasis] = useState<'accrual' | 'cash'>('accrual');

  useEffect(() => {
    if (currentOrganization) {
      loadVatReturns();
      setPeriods(getVatPeriods(currentOrganization.id));
    }
  }, [currentOrganization]);

  const loadVatReturns = async () => {
    if (!currentOrganization) return;
    setLoading(true);
    try {
      const response = await listVatReturns(currentOrganization.id, { limit: 50 });
      setVatReturns(response.returns);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load VAT returns');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewPeriod = async (period: VatPeriod) => {
    if (!currentOrganization) return;
    setSelectedPeriod(period);
    setLoading(true);
    try {
      const calculation = await previewVatCalculation(
        currentOrganization.id,
        period.startDate,
        period.endDate,
        reportingBasis
      );
      setPreview(calculation);
      setActiveTab('wizard');
      setWizardStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview VAT calculation');
    } finally {
      setLoading(false);
    }
  };

  const handleViewVatReturn = (vatReturnId: string) => {
    setSelectedVatReturnId(vatReturnId);
    setActiveTab('detail');
  };

  const handleCreateVatReturn = async () => {
    if (!currentOrganization || !selectedPeriod) return;
    setLoading(true);
    try {
      await createVatReturn({
        organization_id: currentOrganization.id,
        period_start: selectedPeriod.startDate,
        period_end: selectedPeriod.endDate,
        reporting_basis: reportingBasis,
      });
      await loadVatReturns();
      setActiveTab('list');
      setWizardStep(1);
      setPreview(null);
      setSelectedPeriod(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create VAT return');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'filed':
        return 'bg-green-100 text-green-800';
      case 'paid':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'preview':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPeriodStatusColor = (status: VatPeriod['status']) => {
    switch (status) {
      case 'filed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'current':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'upcoming':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">ALV-ilmoitukset</h1>
        <p className="text-gray-600 mt-1">
          Hallitse arvonlisäveroilmoituksia ja seuraa ilmoituskausia
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'calendar'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📅 ALV-kalenteri
          </button>
          <button
            onClick={() => setActiveTab('list')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📋 Ilmoitukset
          </button>
          {preview && (
            <button
              onClick={() => setActiveTab('wizard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'wizard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              🧙‍♂️ Uusi ilmoitus
            </button>
          )}
        </nav>
      </div>

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              ALV-ilmoituskaudet
            </h2>
            <p className="text-gray-600 mb-6">
              Valitse kausi luodaksesi uuden ALV-ilmoituksen. Eräpäivät ovat arvioita.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {periods.map((period) => (
                <div
                  key={period.startDate}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${getPeriodStatusColor(
                    period.status
                  )}`}
                  onClick={() => handlePreviewPeriod(period)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg">{period.label}</h3>
                    <span className="text-xs uppercase font-medium px-2 py-1 rounded bg-white bg-opacity-50">
                      {period.status === 'overdue' && '⚠️ Myöhässä'}
                      {period.status === 'current' && '📋 Käynnissä'}
                      {period.status === 'upcoming' && '📅 Tulossa'}
                      {period.status === 'filed' && '✅ Ilmoitettu'}
                    </span>
                  </div>
                  <p className="text-sm opacity-75">
                    {new Date(period.startDate).toLocaleDateString('fi-FI')} -{' '}
                    {new Date(period.endDate).toLocaleDateString('fi-FI')}
                  </p>
                  <p className="text-sm mt-2">
                    <span className="font-medium">Eräpäivä:</span>{' '}
                    {new Date(period.dueDate).toLocaleDateString('fi-FI')}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
              <span>Myöhässä</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
              <span>Käynnissä</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></div>
              <span>Tulossa</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
              <span>Ilmoitettu</span>
            </div>
          </div>
        </div>
      )}

      {/* List Tab */}
      {activeTab === 'list' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              ALV-ilmoitukset
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="animate-spin inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full mr-2"></div>
              Ladataan...
            </div>
          ) : vatReturns.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p className="text-lg mb-2">Ei ALV-ilmoituksia</p>
              <p className="text-sm">
                Luo uusi ilmoitus valitsemalla kausi ALV-kalenterista
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kausi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tila
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ALV maksettava
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Luotu
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Toiminnot
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vatReturns.map((vatReturn) => (
                    <tr key={vatReturn.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(vatReturn.periodStart).toLocaleDateString('fi-FI')} -{' '}
                          {new Date(vatReturn.periodEnd).toLocaleDateString('fi-FI')}
                        </div>
                        <div className="text-sm text-gray-500">
                          {vatReturn.reportingBasis === 'accrual' ? 'Suoriteperuste' : 'Maksuperuste'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            vatReturn.status
                          )}`}
                        >
                          {vatReturn.status === 'draft' && t('common.draft')}
                          {vatReturn.status === 'preview' && 'Esikatselu'}
                          {vatReturn.status === 'filed' && 'Ilmoitettu'}
                          {vatReturn.status === 'paid' && 'Maksettu'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm font-medium ${
                            vatReturn.netVat >= 0 ? 'text-red-600' : 'text-green-600'
                          }`}
                        >
                          {vatReturn.netVat >= 0 ? '+' : ''}
                          {vatReturn.netVat.toFixed(2)} €
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(vatReturn.createdAt).toLocaleDateString('fi-FI')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleViewVatReturn(vatReturn.id)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Näytä erittely
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Detail Tab */}
      {activeTab === 'detail' && selectedVatReturnId && (
        <VatBreakdownDetail
          vatReturnId={selectedVatReturnId}
          onBack={() => {
            setActiveTab('list');
            setSelectedVatReturnId(null);
          }}
          onRecalculated={() => {
            loadVatReturns();
          }}
        />
      )}

      {/* Wizard Tab */}
      {activeTab === 'wizard' && preview && selectedPeriod && (
        <div className="space-y-6">
          {/* Wizard Steps */}
          <div className="flex items-center space-x-4">
            <div
              className={`flex items-center ${wizardStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                  wizardStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'
                }`}
              >
                1
              </div>
              <span className="ml-2 font-medium">Asetukset</span>
            </div>
            <div className="flex-1 h-px bg-gray-300"></div>
            <div
              className={`flex items-center ${wizardStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                  wizardStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'
                }`}
              >
                2
              </div>
              <span className="ml-2 font-medium">Esikatselu</span>
            </div>
            <div className="flex-1 h-px bg-gray-300"></div>
            <div
              className={`flex items-center ${wizardStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                  wizardStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'
                }`}
              >
                3
              </div>
              <span className="ml-2 font-medium">Vahvistus</span>
            </div>
          </div>

          {/* Step 1: Settings */}
          {wizardStep === 1 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                ALV-ilmoituksen asetukset
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ilmoituskausi
                  </label>
                  <p className="text-gray-900 font-medium">
                    {selectedPeriod.label}: {new Date(selectedPeriod.startDate).toLocaleDateString('fi-FI')} -{' '}
                    {new Date(selectedPeriod.endDate).toLocaleDateString('fi-FI')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ilmoitusperuste
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="accrual"
                        checked={reportingBasis === 'accrual'}
                        onChange={(e) => setReportingBasis(e.target.value as 'accrual' | 'cash')}
                        className="mr-2"
                      />
                      <span>Suoriteperuste (laskutusperuste)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="cash"
                        checked={reportingBasis === 'cash'}
                        onChange={(e) => setReportingBasis(e.target.value as 'accrual' | 'cash')}
                        className="mr-2"
                      />
                      <span>Maksuperuste</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setActiveTab('calendar');
                    setPreview(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Peruuta
                </button>
                <button
                  onClick={() => handlePreviewPeriod(selectedPeriod)}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? t('common.loading') : t('vat.preview')}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {wizardStep === 2 && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  ALV-ilmoituksen esikatselu
                </h2>

                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Myynnit yhteensä</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {preview.summary.totalSales.toFixed(2)} €
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Ostot yhteensä</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {preview.summary.totalPurchases.toFixed(2)} €
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">ALV maksettava</p>
                    <p className="text-2xl font-bold text-red-600">
                      {preview.summary.vatPayable.toFixed(2)} €
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">ALV palautettava</p>
                    <p className="text-2xl font-bold text-green-600">
                      {preview.summary.vatReceivable.toFixed(2)} €
                    </p>
                  </div>
                </div>

                {/* Net VAT */}
                <div
                  className={`p-4 rounded-lg mb-6 ${
                    preview.summary.netVat >= 0 ? 'bg-red-50' : 'bg-green-50'
                  }`}
                >
                  <p className="text-sm font-medium text-gray-700">
                    {preview.summary.netVat >= 0
                      ? 'ALV maksettava verottajalle:'
                      : 'ALV palautettava verottajalta:'}
                  </p>
                  <p
                    className={`text-3xl font-bold ${
                      preview.summary.netVat >= 0 ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    {Math.abs(preview.summary.netVat).toFixed(2)} €
                  </p>
                </div>

                {/* VAT Lines */}
                <h3 className="font-semibold text-gray-800 mb-3">ALV-erittely</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Koodi
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Kuvaus
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Myynnit
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          ALV myynnit
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Ostot
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          ALV ostot
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {preview.lines.map((line) => (
                        <tr key={line.id}>
                          <td className="px-4 py-2 text-sm font-medium">{line.vatCode}</td>
                          <td className="px-4 py-2 text-sm">{line.description}</td>
                          <td className="px-4 py-2 text-sm text-right">
                            {line.salesAmount.toFixed(2)} €
                          </td>
                          <td className="px-4 py-2 text-sm text-right">
                            {line.vatOnSales.toFixed(2)} €
                          </td>
                          <td className="px-4 py-2 text-sm text-right">
                            {line.purchasesAmount.toFixed(2)} €
                          </td>
                          <td className="px-4 py-2 text-sm text-right">
                            {line.vatOnPurchases.toFixed(2)} €
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setWizardStep(1)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  ← Takaisin
                </button>
                <div className="space-x-3">
                  <button
                    onClick={() => {
                      setActiveTab('calendar');
                      setPreview(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Peruuta
                  </button>
                  <button
                    onClick={handleCreateVatReturn}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? t('common.saving') : t('vatReturns.createVatReturn')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
